import {
	convertFromLocalTimezone,
	convertToLocalTimezone,
	EventsResponse,
	FullEvent,
	FullMarketplaceItem,
	FullPost,
	FullPromotedPost,
	Post,
	PostTypes,
	SharingData,
	StringServiceResponse
} from 'model';
import { BaseApi } from './BaseAPI';
import { FeedApi } from './FeedApi.js';

const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const baseURL = 'post/';

// This can be statically imported and referenced, it is updated when the application loads
// by LandingPage using useGetInstallationTimeZoneQuery
export let installationTZ = 'America/Chicago';

/**
 * Amend the eventDate sent to the backend to be properly offset. This corrects an issue where
 * the installationTZ and the localTZ are different, and the eventDate is in the localTS. Almost certainly
 * the user intended the eventDate to be in the installationTZ. This adjusts the eventDate to be the same time
 * as selected, but in the installationTZ.
 * @param { Post } post post with event date in local time
 * @returns { Post } post with amended eventDate
 */
export function amendMutationEventDate(post: Post): Post {
	if (post.postType === 'event') {
		post = {
			...post,
			eventDate: convertFromLocalTimezone(post.eventDate, localTZ, installationTZ),
			eventEndDate:
				post.eventEndDate === '' ? '' : convertFromLocalTimezone(post.eventEndDate, localTZ, installationTZ)
		};
	}
	return post;
}

/**
 * Amend query results to have an event date in local time. This handled an issue where the installationTZ
 * and the localTZ are different. The eventDate is stored in UTC, and would accurately be converted to the local
 * time, but would be incorrect, as the event is happening at the selected time in the installationTZ.
 * @param { Post } post Post with event data in ISO UTC
 * @returns { Post } post with eventDate in local time
 */
export function amendQueryEventDate(post: FullPost): FullPost {
	if (post.postType === 'event') {
		post = {
			...post,
			eventDate: convertToLocalTimezone(post.eventDate, localTZ, installationTZ),
			eventEndDate:
				post.eventEndDate === '' ? '' : convertToLocalTimezone(post.eventEndDate, localTZ, installationTZ)
		} as FullPost;
	}
	post = {
		...post,
		promotionStartDate: !post.promotionStartDate
			? post.promotionStartDate
			: convertToLocalTimezone(post.promotionStartDate, localTZ, installationTZ),
		promotionEndDate: !post.promotionEndDate
			? post.promotionEndDate
			: convertToLocalTimezone(post.promotionEndDate, localTZ, installationTZ)
	} as FullPost;
	return post;
}

/**
 * Update a post's fields associated with adding/removing the user's like from it (i.e. feedback & engagement.likeCount)
 * @param { FullPost } post the post being updated
 * @param { boolean } like the like value from the udpate
 * @param { boolean } dislike the dislike value from the update
 * @returns { FullPost } the updated post
 */
function updatePostLikeFields(post: FullPost, like: boolean, dislike: boolean): FullPost {
	const likeCount = post.engagement.likeCount + (like && !dislike ? 1 : !like || dislike ? -1 : 0);
	const feedback: 0 | 1 | -1 = like ? 1 : dislike ? -1 : 0;
	return {
		...post,
		feedback,
		engagement: {
			...post.engagement,
			likeCount
		}
	};
}

export const PostApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		getPost: builder.query<FullPost, Post['_id'] | undefined>({
			query: (id: Post['_id']) => ({
				url: `${baseURL}${id}`
			}),
			transformResponse: (response: FullPost) => {
				return amendQueryEventDate(response);
			},
			providesTags: (result, error, arg) => [{ type: 'Post', id: arg }]
		}),

		// This will get even posts that are marked as deleted
		getPostAdmin: builder.query<FullPost, Post['_id'] | undefined>({
			query: (id: Post['_id']) => ({
				url: `${baseURL}admin/${id}`
			}),
			transformResponse: (response: FullPost) => {
				return amendQueryEventDate(response);
			},
			providesTags: (result, error, arg) => [{ type: 'Post', id: arg }]
		}),

		addPostImage: builder.mutation<FullPost, { document: FullPost; data: {} }>({
			query: ({ document, data }) => {
				return {
					url: `${baseURL}image/${document._id}`,
					method: 'POST',
					body: data
				};
			},
			invalidatesTags: (...args) => [{ type: 'Post', id: args[2].document._id }]
		}),

		deletePost: builder.mutation<Post, Post['_id']>({
			query: (id: Post['_id']) => ({
				url: `${baseURL}${id}/`,
				method: 'DELETE'
			}),
			invalidatesTags: () => [{ type: 'Post' }]
		}),

		// This marks a post as deleted, but does not delete it.
		setDeletedPost: builder.mutation<Post, { id: Post['_id']; deleted: boolean }>({
			query: ({ id, deleted }) => ({
				url: `${baseURL}setDeleted/${id}/${deleted}`,
				method: 'POST'
			}),
			invalidatesTags: () => [{ type: 'Post' }]
		}),

		deletePostImage: builder.mutation<FullPost, { document: FullPost; imageName?: string }>({
			query: ({ document, imageName }) => ({
				url: `${baseURL}image/${document._id}/${imageName}`,
				method: 'DELETE'
			}),
			invalidatesTags: (...args) => [{ type: 'Post', id: args[2].document._id }]
		}),

		searchPostByTopic: builder.query<FullPost[], string>({
			query: (topic: string) => ({
				url: `${baseURL}topic/${topic}`
			}),
			providesTags: (result, error, arg) => (!result ? [] : result?.map(post => ({ type: 'Post', id: post._id })))
		}),

		recentPosts: builder.query<FullPost[], { postType: string; daysBack: number }>({
			query: ({ postType, daysBack }) => ({
				url: `${baseURL}recent/${postType}/${daysBack}`
			}),
			providesTags: (result, error, arg) => (!result ? [] : result?.map(post => ({ type: 'Post', id: post._id })))
		}),

		getFavoritePosts: builder.query<
			{ posts: FullPost[]; ratings: number[] },
			{ postType: string; dayBack: number }
		>({
			query: ({ postType, dayBack }) => ({
				url: `${baseURL}favorite/${postType}/${dayBack}`
			}),
			providesTags: (result, error, arg) =>
				!result ? [] : result?.posts.map(post => ({ type: 'Post', id: post._id }))
		}),

		searchPostByAuthor: builder.query<FullPost[], string>({
			query: (author: string) => ({
				url: `${baseURL}author/${author}`
			}),
			providesTags: (result, error, arg) => (!result ? [] : result?.map(post => ({ type: 'Post', id: post._id })))
		}),

		countPostsByAuthor: builder.query<StringServiceResponse, string>({
			query: (author: string) => ({
				url: `${baseURL}author/counts/${author}`
			})
		}),

		searchPostTopics: builder.query<{ key: string; value: number }[], string>({
			query: (search: string) => ({
				url: `${baseURL}topicsearch/${search}`
			})
		}),

		createPost: builder.mutation<FullPost, { post: Post }>({
			query: ({ post }) => ({
				url: `${baseURL}create`,
				method: 'POST',
				body: {
					post: amendMutationEventDate(post)
				}
			}),
			invalidatesTags: [{ type: 'Post' }]
		}),

		ratePost: builder.mutation<FullPost, { post: FullPost; like: boolean; dislike: boolean }>({
			query: ({ post, like, dislike }) => ({
				url: `${baseURL}rate`,
				method: 'POST',
				body: { post, like, dislike }
			}),
			async onQueryStarted({ like, dislike, post: { _id } }, { dispatch, queryFulfilled }) {
				// Pessmisitic update to the likeCount & feedback of the affected post
				try {
					await queryFulfilled;
					dispatch(
						FeedApi.util.updateQueryData('getFeed', undefined, feed =>
							feed.map(item => (item._id === _id ? updatePostLikeFields(item, like, dislike) : item))
						)
					);
					dispatch(
						PostApi.util.updateQueryData('getPost', _id, existing =>
							updatePostLikeFields(existing, like, dislike)
						)
					);
				} catch (e) {
					// eslint-disable-next-line no-console
					console.log(e);
				}
			}
		}),

		updateClosedForComments: builder.mutation<FullPost, { postid: Post['_id']; closed: boolean }>({
			query: ({ postid, closed }) => ({
				url: `${baseURL}/updateClosedForComments/${postid}/${closed}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (...args) => [{ type: 'Post' }, { type: 'Post', id: args[2].postid }]
		}),

		updateDisplayComments: builder.mutation<FullPost, { postid: Post['_id']; display: boolean }>({
			query: ({ postid, display }) => ({
				url: `${baseURL}updateDisplayComments/${postid}/${display}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (...args) => [{ type: 'Post' }, { type: 'Post', id: args[2].postid }]
		}),

		updatePost: builder.mutation<FullPost, { post: FullPost }>({
			query: ({ post }) => {
				return {
					url: `${baseURL}update`,
					method: 'POST',
					body: {
						post: amendMutationEventDate(post)
					}
				};
			},
			invalidatesTags: (...args) => [{ type: 'Post' }, { type: 'Post', id: args[2].post._id }]
		}),
		getInstallationTimeZone: builder.query<StringServiceResponse, void>({
			query: () => ({
				url: `${baseURL}timezone`
			}),
			providesTags: [],
			transformResponse: (response: StringServiceResponse) => {
				// This is really pretty crappy
				installationTZ = response.response;
				return response;
			}
		}),
		getSocialSharing: builder.query<SharingData, string>({
			query: (website: string) => ({
				url: `${baseURL}social`,
				body: { url: website },
				method: 'POST'
			})
		}),
		getUpcomingEvents: builder.query<EventsResponse, {}>({
			query: () => ({
				url: `${baseURL}events/180`
			}),
			transformResponse: (response: EventsResponse) => {
				response.events = response.events.map(event => amendQueryEventDate(event)) as FullEvent[];
				return response;
			},
			providesTags: result =>
				!result || !result.events
					? []
					: result.events.map(event => {
							return { type: 'Post', id: event._id };
					  })
		}),
		searchPost: builder.query<FullPost[], { search: string; postType: PostTypes }>({
			query: ({ search, postType }) => ({
				url: `${baseURL}search/${search}/${postType}`
			}),
			providesTags: (result, error, arg) => (!result ? [] : result?.map(post => ({ type: 'Post', id: post._id })))
		}),
		trackPostView: builder.mutation<any, { postId: Post['_id'] }>({
			query: ({ postId }) => ({
				url: `${baseURL}trackView`,
				method: 'POST',
				body: { postId }
			})
		}),
		updateMarketplaceItemClosed: builder.mutation<FullMarketplaceItem, { postid: Post['_id']; close: boolean }>({
			query: ({ postid, close }) => ({
				url: `${baseURL}marketplace/updateClosed/${postid}/${close}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (...args) => [{ type: 'Post' }, { type: 'Post', id: args[2].postid }]
		}),
		getPromoted: builder.query<FullPromotedPost[], void>({
			query: () => ({
				url: `${baseURL}/promoted/`,
				method: 'GET'
			})
		})
	})
});

export const {
	useLazySearchPostTopicsQuery,
	useSearchPostTopicsQuery,
	useSearchPostByTopicQuery,
	useGetPostQuery,
	useSearchPostByAuthorQuery,
	useCreatePostMutation,
	useUpdatePostMutation,
	useRatePostMutation,
	useAddPostImageMutation,
	useDeletePostMutation,
	useDeletePostImageMutation,
	useUpdateClosedForCommentsMutation,
	useUpdateDisplayCommentsMutation,
	useCountPostsByAuthorQuery,
	useLazyGetPostQuery,
	useSetDeletedPostMutation,
	useGetPostAdminQuery,
	useLazyGetPostAdminQuery,
	useGetInstallationTimeZoneQuery,
	useGetFavoritePostsQuery,
	useLazyGetSocialSharingQuery,
	useGetUpcomingEventsQuery,
	useLazySearchPostQuery,
	useTrackPostViewMutation,
	useUpdateMarketplaceItemClosedMutation,
	useRecentPostsQuery,
	useGetPromotedQuery
} = PostApi;
